import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getPartById, deletePart } from "@/services/partsService";
import type { SparePart, PartBrand, PartCategory } from "@oriental-ramirez/shared";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Package,
  DollarSign,
  Box,
  Tag,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { queryKeys } from "@/lib/queryKeys";
import { useState } from "react";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function toDate(val: unknown): Date | null {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof (val as { toDate?: () => Date }).toDate === "function")
    return (val as { toDate(): Date }).toDate();
  if (typeof val === "object" && val !== null && "seconds" in val)
    return new Date((val as { seconds: number }).seconds * 1000);
  return null;
}

function formatDate(val: unknown): string {
  const d = toDate(val);
  if (!d) return "--";
  return d.toLocaleDateString("es-DO", { day: "2-digit", month: "long", year: "numeric" });
}

function formatCurrency(amount: number | undefined, currency?: string): string {
  if (amount == null) return "--";
  const code = currency === "DOP" ? "DOP" : "USD";
  return new Intl.NumberFormat("es-DO", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 0,
  }).format(amount);
}

function Spec({ label, value }: { label: string; value?: string | number | boolean | null }) {
  if (value == null || value === "" || value === false) return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/50 last:border-0">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">
        {typeof value === "boolean" ? "Sí" : String(value)}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Image gallery                                                     */
/* ------------------------------------------------------------------ */

function ImageGallery({ images }: { images: string[] }) {
  const [current, setCurrent] = useState(0);
  if (images.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="relative aspect-square overflow-hidden rounded-lg border border-border bg-muted">
        <img src={images[current]} alt="" className="h-full w-full object-contain" />
        {images.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background"
              onClick={() => setCurrent((c) => (c - 1 + images.length) % images.length)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background"
              onClick={() => setCurrent((c) => (c + 1) % images.length)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-background/80 px-2.5 py-0.5 text-xs tabular-nums">
              {current + 1} / {images.length}
            </div>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`shrink-0 h-14 w-14 rounded-md border-2 overflow-hidden transition-colors ${
                i === current ? "border-primary" : "border-transparent hover:border-muted-foreground/30"
              }`}
            >
              <img src={img} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

export function PartDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: part, isLoading } = useQuery({
    queryKey: queryKeys.part(id!),
    queryFn: () => getPartById(id!),
    enabled: Boolean(id),
  });

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm("¿Eliminar este repuesto? Esta acción no se puede deshacer.")) return;
    try {
      await deletePart(id);
      toast.success("Repuesto eliminado");
      await queryClient.invalidateQueries({ queryKey: queryKeys.parts });
      navigate("/parts");
    } catch {
      toast.error("Error al eliminar el repuesto");
    }
  };

  const brand = part?.brand as PartBrand | undefined;
  const category = part?.category as PartCategory | undefined;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/parts"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Repuesto</h1>
        </div>
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    );
  }

  if (!part) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/parts"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Repuesto</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">Repuesto no encontrado</p>
            <Button asChild className="mt-4">
              <Link to="/parts">Volver a repuestos</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stockLevel =
    part.stock == null ? null : part.stock <= 0 ? "out" : part.stock <= 5 ? "low" : "ok";

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/parts">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Volver</span>
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl font-semibold">{part.name}</h1>
              {part.featured && (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">Destacado</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {brand?.name ?? ""}
              {part.sku ? ` · SKU: ${part.sku}` : ""}
              {category?.name ? ` · ${category.name}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" asChild>
            <Link to={`/parts/edit/${part.id}`}>
              <Pencil className="h-4 w-4" />
              Editar
            </Link>
          </Button>
          <Button variant="outline" className="gap-2 text-destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            Eliminar
          </Button>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left column */}
        <div className="space-y-6">
          {/* Images */}
          {(part.images?.length ?? 0) > 0 && (
            <Card>
              <CardContent className="pt-6">
                <ImageGallery images={part.images!} />
              </CardContent>
            </Card>
          )}

          {/* Description */}
          {part.description && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Descripción</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{part.description}</p>
              </CardContent>
            </Card>
          )}

          {/* Compatibility */}
          {(part.compatibleWith?.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Wrench className="h-4 w-4" />
                  Vehículos compatibles
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {part.compatibleWith!.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Badge variant="secondary">
                        {[c.brand, c.model].filter(Boolean).join(" ")}
                        {c.yearFrom || c.yearTo
                          ? ` (${c.yearFrom ?? "?"}–${c.yearTo ?? "?"})`
                          : ""}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <DollarSign className="h-4 w-4" />
                Precio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">
                  {formatCurrency(part.price, part.currency)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{part.currency ?? "USD"}</p>
              </div>
            </CardContent>
          </Card>

          {/* Stock */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Box className="h-4 w-4" />
                Inventario
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold">{part.stock ?? "--"}</span>
                {stockLevel === "out" && (
                  <Badge variant="destructive">Agotado</Badge>
                )}
                {stockLevel === "low" && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Stock bajo</Badge>
                )}
                {stockLevel === "ok" && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Disponible</Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Tag className="h-4 w-4" />
                Información
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <Spec label="SKU" value={part.sku} />
              <Spec label="Marca" value={brand?.name} />
              <Spec label="Categoría" value={category?.name} />
              <Spec label="Destacado" value={part.featured} />
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Creado: {formatDate((part as unknown as Record<string, unknown>).createdAt)}
                </div>
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Actualizado: {formatDate((part as unknown as Record<string, unknown>).updatedAt)}
                </div>
                <div className="flex items-center gap-1.5 font-mono text-xs">
                  ID: {part.id}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
